import { useContext, useEffect, useState, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { useNavigate, useLocation, matchPath } from 'react-router'

// evertel
import { useService } from '@evertel/di'
import { DrawerController } from '@evertel/message'
import { MultilinePlaceholder, useUI, FormInput, Icon, InputGroupText } from '@evertel/web/ui'
import { SessionService } from '@evertel/session/SessionService'
import { UnreadCountsState, UserSettingsController } from '@evertel/blue-user'
import { useJoinRoom } from '@evertel/web/room'
import { useCreateThread } from '@evertel/web/thread'
import { DrawerNavDropdownLink, DrawerNavItemLink, DrawerNavRoomLink, DrawerNavThreadLink } from '../elements'
import { DrawerNavigationContext } from '../DrawerNavigationContext'


const MessagesDrawer: React.FC = observer(() => {

    const sessionService = useService(SessionService, [])
    const drawerController = useService(DrawerController, [sessionService.selectedDepartmentId])
    const userSettingsController = useService(UserSettingsController, [])
    const unreadState = useService(UnreadCountsState, [])

    // Section visibility state lives in DrawerController (shared with mobile,
    // persisted under 'drawerSectionVisibility')
    const {
        interagency: interagencyVisible,
        agencyWide: agencywideVisible,
        team: teamVisible,
        threads: threadsVisible
    } = drawerController.sectionVisibility

    // Read each controller getter once per render (they sort on every access)
    const interAgencyRooms = drawerController.interAgencyRooms
    const agencyWideRooms = drawerController.agencyWideRooms
    const teamRooms = drawerController.teamRooms
    const sortedThreads = drawerController.sortedThreads

    const navigate = useNavigate()
    const location = useLocation()
    const { openJoinRoom } = useJoinRoom()
    const { addToast, leftNavOpen } = useUI()
    const { openCreateThread } = useCreateThread()
    const { updateNavList } = useContext(DrawerNavigationContext)

    const [isLoading, setIsLoading] = useState(false)
    const [filterValue, setFilterValue] = useState('')

    // Clear filter when drawer is closed
    useEffect(() => {
        if (!leftNavOpen) {
            setFilterValue('')
        }
    }, [leftNavOpen])

    // Filter rooms and threads based on filterValue
    const filterLower = filterValue.toLowerCase()

    const filteredInteragencyRooms = useMemo(() => {
        if (!filterValue) return interAgencyRooms
        return interAgencyRooms?.filter(room =>
            room.name?.toLowerCase().includes(filterLower)
        )
    }, [interAgencyRooms, filterLower])

    const filteredAgencyWideRooms = useMemo(() => {
        if (!filterValue) return agencyWideRooms
        return agencyWideRooms?.filter(room =>
            room.name?.toLowerCase().includes(filterLower)
        )
    }, [agencyWideRooms, filterLower])

    const filteredTeamRooms = useMemo(() => {
        if (!filterValue) return teamRooms
        return teamRooms?.filter(room =>
            room.name?.toLowerCase().includes(filterLower)
        )
    }, [teamRooms, filterLower])

    const filteredThreads = useMemo(() => {
        if (!filterValue) return sortedThreads
        return sortedThreads?.filter(thread =>
            thread.name?.toLowerCase().includes(filterLower)
        )
    }, [sortedThreads, filterLower])

    useState(() => {
        setIsLoading(true)
        Promise.all([
            drawerController.fetchActiveRooms(),
            drawerController.fetchActiveThreads(),
            unreadState.fetchUnreadCounts()

        ]).then(() => {
            setIsLoading(false)
            updateNavList() // Signal navigation can be built
        })
    })

    useEffect(() => {
        drawerController.startPoll()

        return function messagesDrawerCleanup() {
            drawerController.stopPoll()
        }
    }, [])

    const closeThread = async (threadId: number, e: Event) => {
        e.stopPropagation()
        try {
            await drawerController.closeThread(threadId)

            sessionService.deleteThreadFromNavigationHistory(threadId)

            const match = matchPath( { path: '/thread/:id' }, location.pathname )

            // only navigate if deleted thread was open
            if (match && match.params.id && (parseInt(match.params.id) === threadId)) {
                navigate('/landing')
            }

        } catch (error: any) {
            addToast({
                color: 'danger',
                message: error.message
            })
        }
    }

    const getRoomIcon = (room: any) => {
        if (room.isSearchable) {
            return 'bolt' // Incident Command room
        }
        if (!(room.options as any)?.departmentMembersCanJoinAsMember) {
            return 'lock' // Private room
        }
        return 'hashtag' // Regular room
    }

    if (isLoading) {
        return (
            <MultilinePlaceholder sections={4} />
        )
    }

    return (
        <>
            <div className="position-relative">
                <FormInput
                    type="text"
                    name="filterRoomsThreads"
                    label="Find a #Room or DM"
                    placeholder="Search..."
                    onChange={(e) => setFilterValue(e.target.value)}
                    value={filterValue}
                    aria-label="Find a #Room or DM"
                    autoComplete="off"
                    data-drawer-nav="item"
                    inputClassName={filterValue ? 'pe-5' : ''}
                />
                {filterValue && (
                    <button
                        type="button"
                        onClick={() => setFilterValue('')}
                        className="btn btn-close position-absolute p-0"
                        style={{
                            right: 10,
                            bottom: 18,
                            zIndex: 10
                        }}
                        aria-label="Clear filter"
                    />
                )}
            </div>

            {/* interagency rooms ----------------------------------------------------------------------------- */}
            {!!interAgencyRooms?.length && (
                <DrawerNavDropdownLink
                    label="Interagency Rooms"
                    isOpen={(interagencyVisible || !!filterValue) && !!filteredInteragencyRooms?.length}
                    onClick={(isOpen) => drawerController.setSectionVisibility('interagency', isOpen)}
                    unreadCount={drawerController.interAgencyRoomsUnreadCountTotal?.count}
                    hasUrgentUnreads={!!drawerController.interAgencyRoomsUnreadCountTotal?.urgent}>

                    {filteredInteragencyRooms?.map(room => {
                        if (room.isArchived) return null
                        const unread = unreadState.getRoomUnread(room.id as number) as any
                        const settings = userSettingsController.roomNotificationSettings[room.id as number]?.roomMessage

                        return (
                            <DrawerNavRoomLink
                                key={room.id}
                                to={`/room/${room.id}`}
                                departmentId={room.departmentId}
                                label={room.name as string}
                                icon={getRoomIcon(room)}
                                unreadCount={unread?.count || 0}
                                hasUrgentUnreads={unread?.urgent}
                                selected={new RegExp(`^/room/${room.id}(/replies/[^/]+)?$`).test(location.pathname)}
                                muted={settings && !settings?.enabled}
                            />
                        )
                    })}
                </DrawerNavDropdownLink>
            )}

            {/* agency-wide rooms ----------------------------------------------------------------------------- */}
            <DrawerNavDropdownLink
                label="Agency-Wide Rooms"
                isOpen={(agencywideVisible || !!filterValue) && !!filteredAgencyWideRooms?.length}
                onClick={(isOpen) => drawerController.setSectionVisibility('agencyWide', isOpen)}
                unreadCount={drawerController.agencyWideRoomsUnreadCountTotal?.count}
                hasUrgentUnreads={!!drawerController.agencyWideRoomsUnreadCountTotal?.urgent}>

                {filteredAgencyWideRooms?.map(room => {
                    if (room.isArchived) return null
                    const unread = unreadState.getRoomUnread(room.id as number) as any
                    const settings = userSettingsController.roomNotificationSettings[room.id as number]?.roomMessage

                    return (
                        <DrawerNavRoomLink
                            key={room.id}
                            to={`/room/${room.id}`}

                            label={room.name as string}
                            icon={getRoomIcon(room)}
                            unreadCount={unread?.count || 0}
                            hasUrgentUnreads={unread?.urgent}
                            selected={new RegExp(`^/room/${room.id}(/replies/[^/]+)?$`).test(location.pathname)}
                            muted={settings && !settings?.enabled}
                        />
                    )
                })}
            </DrawerNavDropdownLink>

            {/* team rooms ------------------------------------------------------------------------------------ */}
            <DrawerNavDropdownLink
                label="Team Rooms"
                isOpen={(teamVisible || !!filterValue) && !!filteredTeamRooms?.length}
                onClick={(isOpen) => drawerController.setSectionVisibility('team', isOpen)}
                unreadCount={drawerController.teamRoomsUnreadCountTotal?.count}
                hasUrgentUnreads={!!drawerController.teamRoomsUnreadCountTotal?.urgent}>

                {filteredTeamRooms?.map(room => {
                    if (room.isArchived) return null
                    const unread = unreadState.getRoomUnread(room.id as number) as any
                    const settings = userSettingsController.roomNotificationSettings[room.id as number]?.roomMessage

                    return (
                        <DrawerNavRoomLink
                            key={room.id}
                            to={`/room/${room.id}`}

                            label={room.name as string}
                            icon={getRoomIcon(room)}
                            unreadCount={unread?.count || 0}
                            hasUrgentUnreads={unread?.urgent}
                            selected={new RegExp(`^/room/${room.id}(/replies/[^/]+)?$`).test(location.pathname)}
                            muted={settings && !settings?.enabled}
                        />
                    )
                })}
                {!filterValue &&
                        <DrawerNavItemLink
                            label="Join Room"
                            icon={{ name: 'circle-plus', type: 'solid' }}
                            onClick={openJoinRoom}
                        />
                }
            </DrawerNavDropdownLink>

            {/* DMs ---------------------------------------------------------------------------------------- */}
            <DrawerNavDropdownLink
                label="Direct Messages"
                isOpen={(threadsVisible || !!filterValue) && !!filteredThreads?.length}
                onClick={(isOpen) => drawerController.setSectionVisibility('threads', isOpen)}
                unreadCount={drawerController.threadsUnreadCountTotal?.count}
                hasUrgentUnreads={!!drawerController.threadsUnreadCountTotal?.urgent}>

                {filteredThreads?.map((thread) => {
                    const unreads = unreadState.getThreadUnread(thread.id as number) as any

                    return (
                        <DrawerNavThreadLink
                            key={thread.id}
                            to={`/thread/${thread.id}`}
                            thread={thread}
                            unreadCount={unreads?.count || 0}
                            hasUrgentUnreads={unreads?.urgent}
                            selected={location.pathname === `/thread/${thread.id}`}
                            onCloseThread={(e: Event) => closeThread(thread.id as number, e)}
                        />
                    )
                })}
                {!filterValue &&
                        <DrawerNavItemLink
                            label="Add Conversation"
                            icon={{ name: 'circle-plus', type: 'solid' }}
                            onClick={openCreateThread}
                        />
                }
            </DrawerNavDropdownLink>
        </>
    )
})

export { MessagesDrawer }
